export type AnalyticsEvent = {
  name: string
  data?: {
    [key: string]: string | number | boolean | string[]
  }
}

export type CTALocation =
  | 'homepage hero plates CTA'
  | 'homepage weekly recipe CTA'
  | 'homepage how it works CTA'
  | 'homepage ready to go section CTA'
  | 'homepage trustpilot CTA'
  | 'homepage bottom of the page CTA'
  | 'header CTA'
  | 'homepage 1 person CTA'
  | 'homepage 2 person CTA'
  | 'homepage family CTA'
  | 'homepage marketing banner'
  | 'add-on carousel'
  | 'BBC homepage hero plates CTA'
  | 'BBC homepage weekly recipe CTA'
  | 'BBC homepage how it works CTA'
  | 'BBC homepage what makes MC stand out CTA'
  | 'BBC homepage trustpilot CTA'
  | 'BBC homepage bottom of the page CTA'
  | 'BBC homepage header CTA'
  | 'BBC homepage 1 person CTA'
  | 'BBC homepage 2 person CTA'
  | 'BBC homepage family CTA'

export type CookieActions = 'reject' | 'accept' | 'preferences' | 'privacy policy'

export type CookieCategories = 'essential' | 'analytics' | 'marketing' | 'diagnostic'

export type CookieConsentChange = 'granted' | 'denied'

export type CookieConsentChanges = {
  [key in CookieCategories]?: CookieConsentChange
}

export type CtaType = 'button' | 'link'

export type DashBoardTabs = 'upcoming' | 'past' | 'calendar' | 'your recipes'

export type DeliveryStatus = 'upcoming' | 'billed' | 'past'

export type DeliveryType = 'scheduled' | 'one off'

type AddOnErrorName =
  | 'product_ids_are_not_unique'
  | 'product_not_found'
  | 'product_is_not_an_add_on'
  | 'product_is_not_published'
  | 'product_type_not_allowed'
  | 'too_many_add_ons_of_product_type'
  | 'too_many_recipes_for_add_ons'
  | 'maximum_number_of_add_ons_exceeded'

type BillingErrorName = 'invalid card info' | 'invalid billing info' | 'invalid coupon'

type ReadyToGoErrorName = 'no_creation_after_order_cutoff_on_the_day_before_the_delivery_date'

export type ErrorName = BillingErrorName | AddOnErrorName | ReadyToGoErrorName

export type ErrorType =
  | 'calendar'
  | 'delivery review'
  | 'basket'
  | 'add-ons page'
  | 'onboarding add-ons page'
  | 'create ready to go order'

export type ExpandFilterProteinOptions = FoodGroup[]

export type ExpandFilterSortOptionNames = 'portion count' | 'protein' | 'recipe sort' | 'none'

export type ExpandFilterSortPortionSizes = '1 person' | '2 person' | 'family'

export type ExpandFilterSortSelected = 'favourites' | 'low carbs' | 'low price' | 'low calorie' | 'quickest'

export type FoodGroup = 'Beef' | 'Dairy' | 'Fish' | 'Lamb' | 'Pork' | 'Poultry' | 'Shellfish' | 'Vegan' | 'Vegetarian'

/**
 * Product types from the API
 *
 * @see https://mindfulchef.postman.co/workspace/MindfulChef-API~3051f434-fb9c-495a-8ebb-bdf416f51665/folder/7544997-871587e4-1729-4359-9172-c43edb9a7dc7?ctx=documentation
 */

export type LinkTypes = 'buy recipe' | 'buy ready to go' | 'edit recipe delivery'

export type OrderTypes = 'recipe' | 'ready to go' | 'both' | 'none'

export type PageSource =
  | 'account ready to go category page'
  | 'account recipes page'
  | 'about you page'
  | 'skip order modal'
  | 'skip survey'
  | 'skip survey change delivery day modal'
  | 'add-on basket'
  | 'add-on details'
  | 'add-ons page'
  | 'add-ons remove modal'
  | 'basket'
  | 'breakfasts page'
  | 'broths and soups page'
  | 'bundles page'
  | 'calendar'
  | 'calendar shortcut'
  | 'change of plans modal'
  | 'choose recipes page'
  | 'choose recipes onboarding'
  | 'confirmation'
  | 'contact us modal'
  | 'contact page'
  | 'delivery issue modal'
  | 'forgot password page'
  | 'frozen desserts page'
  | 'gift card page'
  | 'healthy recipes library page'
  | 'homepage'
  | 'login page'
  | 'meals page'
  | 'offer modal'
  | 'onboarding add-ons page'
  | 'onboarding add-on details'
  | 'onboarding post purchase upsell details'
  | 'onboarding post purchase upsell page'
  | 'upcoming order page'
  | 'order confirmation page'
  | 'order details page'
  | 'other'
  | 'payment page'
  | 'past delivery page'
  | 'past orders'
  | 'post purchase upsell page'
  | 'post purchase upsell details'
  | 'preference page'
  | 'primary cancellation reason modal'
  | 'product page'
  | 'reactivate modal'
  | 'ready to go basket'
  | 'ready to go page'
  | 'ready to go category page'
  | 'recipe onboarding confirmation'
  | 'recipe page'
  | 'recipe page upsell card'
  | 'recipes details'
  | 'recipes portion switcher'
  | 'recipe onboarding success'
  | 'recipes'
  | 'referral page'
  | 'reset password page'
  | 'review page'
  | 'secondary cancellation reason modal'
  | 'shipping page'
  | 'skip recipe modal'
  | 'smoothies page'
  | 'snacks page'
  | 'success page ratings modal'
  | 'support page'
  | 'upcoming delivery page'
  | 'upcoming orders'
  | 'upsell page basket'
  | 'your details page'
  | 'your recipes'

export type PauseForPeriod = {
  pausePeriod: string
  displayFromDate: string
  pauseUntilDate: string
}

export type Rating = 1 | 2 | 3 | 4 | 5

export type RecipeRoundelType =
  | 'protein'
  | 'easy nights'
  | 'special'
  | 'christmas'
  | 'health focus'
  | 'low carbon'
  | 'quick'
  | 'rating'
  | 'new'
  | 'bbc good food'
  | 'family classics'

export type ReferralChannel = 'facebook' | 'messenger' | 'whatsapp' | 'email' | 'copy link'

export type RewardStatus = 'delivered' | 'applied' | 'available' | 'expired' | 'billed'

export type Section = 'top nav' | 'footer' | 'none'

export type SkipSurveyReason = 'Away' | 'Busy' | 'Recipes' | 'Cost' | 'Other'

export enum ProductType {
  RECIPES = 'recipes',
  MEAL = 'meal',
  SMOOTHIE = 'smoothie',
  FROZEN_DESSERTS = 'frozen_desserts',
  BREAKFASTS = 'breakfasts',
  SNACKS = 'snacks',
  BUNDLES = 'bundled_products',
}
